import React, { useMemo, useState, useEffect } from "react";
import { clsx } from "clsx";
import {
  useLoanDashboard,
  LOSPlatform,
  DrawdownBatch,
} from "../../../../contexts";
import { Divider, Modal, Button } from "../../../../components";
import {
  ArrowLeftIcon,
  FirstAbuDhabiBankLogo,
  CopyToClipboard,
  GreenTickIcon,
  InfoIcon,
} from "../../../../assets/icons";
import { formatAmount, withProfitRateTerminology } from "../../../../utils";
import { useLoanProductConfigFlags } from "../../../../hooks/useLoanProductConfigFlags";
import {
  LoanProductType,
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusLabelsForCreditLine,
  LoanApplicationStatus,
} from "../../../../hooks/api/types";
import { useGetLoanDrawdowns } from "../../../../hooks/api";
import AdditionalInfoForm from "../AdditionalInfoForm";
import InvoiceListing from "../InvoiceListing";
import { getLoanDateDisplay, shouldIssueDrawdown } from "../service";
import {
  IBAN_TEXT,
  IN_REVIEW_STATUSES,
  PROGRESS_BAR_STATUSES,
  INTEREST_RATE_STATUSES,
  REQUESTED_STATUSES,
  PROPOSED_STATUSES,
  APPROVED_STATUSES,
  ACTIVE_STATUSES,
  LATE_FEE_STATUSES,
} from "../constants";
import { LoanDashboardProgress } from "../../LoanDashboardProgress";

type DisplayItem = {
  label: string;
  valueType:
    | "requestedFacility"
    | "approvedFacility"
    | "availableAmount"
    | "duration"
    | "outstandingAmount"
    | "lateFee";
  showCurrency?: boolean;
  showProgressBar?: boolean;
  showInterestRate?: boolean;
  headingColor?: string;
  textColor?: string;
};

function withFlags(
  items: DisplayItem[],
  status: LoanApplicationCaseStatus
): DisplayItem[] {
  return items.map((item) => ({
    ...item,
    showProgressBar:
      item.showProgressBar || PROGRESS_BAR_STATUSES.includes(status),
    showInterestRate:
      item.showInterestRate || INTEREST_RATE_STATUSES.includes(status),
  }));
}

const DISPLAY_PRESETS: Record<string, DisplayItem[]> = {
  REQUESTED: [
    {
      label: "Requested Facility",
      valueType: "requestedFacility",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Duration",
      valueType: "duration",
      showCurrency: false,
      showProgressBar: false,
      showInterestRate: false,
    },
  ],
  PROPOSED: [
    {
      label: "Proposed Facility",
      valueType: "approvedFacility",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Duration",
      valueType: "duration",
      showCurrency: false,
      showProgressBar: false,
      showInterestRate: false,
    },
  ],
  APPROVED: [
    {
      label: "Approved Facility",
      valueType: "approvedFacility",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Duration",
      valueType: "duration",
      showCurrency: false,
      showProgressBar: false,
      showInterestRate: false,
    },
  ],
  ACTIVE: [
    {
      label: "Approved Facility",
      valueType: "approvedFacility",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Outstanding Amount",
      valueType: "outstandingAmount",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
  ],
  AWAITING_ACTIVATION: [
    {
      label: "Approved Facility",
      valueType: "approvedFacility",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Available Amount",
      valueType: "availableAmount",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
  ],
  LATE_FEE: [
    {
      label: "Approved Facility",
      valueType: "approvedFacility",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Outstanding Amount",
      valueType: "outstandingAmount",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
    },
    {
      label: "Late Fee",
      valueType: "lateFee",
      showCurrency: true,
      showProgressBar: false,
      showInterestRate: false,
      headingColor: "#A2191F",
      textColor: "#A2191F",
    },
  ],
};

const DEFAULT_DISPLAY_ITEMS: DisplayItem[] = [
  {
    label: "Requested Facility",
    valueType: "requestedFacility",
    showCurrency: true,
  },
  {
    label: "Duration",
    valueType: "duration",
    showCurrency: false,
    showProgressBar: false,
  },
];

const CASE_STATUS_DISPLAY_MAP: Partial<
  Record<LoanApplicationCaseStatus, DisplayItem[]>
> = {};

REQUESTED_STATUSES.forEach(
  (status) =>
    (CASE_STATUS_DISPLAY_MAP[status] = withFlags(
      DISPLAY_PRESETS.REQUESTED,
      status
    ))
);

PROPOSED_STATUSES.forEach(
  (status) =>
    (CASE_STATUS_DISPLAY_MAP[status] = withFlags(
      DISPLAY_PRESETS.PROPOSED,
      status
    ))
);

APPROVED_STATUSES.forEach(
  (status) =>
    (CASE_STATUS_DISPLAY_MAP[status] = withFlags(
      DISPLAY_PRESETS.APPROVED,
      status
    ))
);

ACTIVE_STATUSES.forEach(
  (status) =>
    (CASE_STATUS_DISPLAY_MAP[status] = withFlags(
      DISPLAY_PRESETS.ACTIVE,
      status
    ))
);

[
  LoanApplicationCaseStatus.SEND_TO_SERVICING,
  LoanApplicationCaseStatus.WAITING_FOR_DISBURSEMENT,
].forEach(
  (status) =>
    (CASE_STATUS_DISPLAY_MAP[status] = withFlags(
      DISPLAY_PRESETS.AWAITING_ACTIVATION,
      status
    ))
);

LATE_FEE_STATUSES.forEach(
  (status) =>
    (CASE_STATUS_DISPLAY_MAP[status] = withFlags(
      DISPLAY_PRESETS.LATE_FEE,
      status
    ))
);

const FacilityOverview: React.FC = () => {
  const {
    state: {
      currentLoanApplication,
      currentLoanApplicationId,
      showAdditionalInfoRequired,
    },
    actions: { update, refetch },
  } = useLoanDashboard();

  const [confirmPaidOpen, setConfirmPaidOpen] = useState(false);
  const [drawdowns, setDrawdowns] = useState<DrawdownBatch[]>([]);
  const [ibanCopied, setIbanCopied] = useState(false);
  const processingFee =
    currentLoanApplication?.processingFee ??
    currentLoanApplication?.priorityDecision?.processingFee;

  const getDrawdowns = useGetLoanDrawdowns(currentLoanApplicationId);

  useEffect(() => {
    if (!currentLoanApplicationId) return;
    const load = async () => {
      const response = await getDrawdowns.mutateAsync(undefined);
      setDrawdowns(response?.drawdownBatches ?? response ?? []);
    };
    load();
  }, [currentLoanApplicationId]);

  const handleCopyIban = () => {
    const el = document.createElement("textarea");
    el.value = IBAN_TEXT;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.focus();
    el.select();
    el.setSelectionRange(0, el.value.length);
    try {
      const copied = document.execCommand("copy");
      if (copied) {
        setIbanCopied(true);
        window.setTimeout(() => setIbanCopied(false), 2000);
      }
    } finally {
      document.body.removeChild(el);
    }
  };

  const handleBack = async () => {
    await update({
      currentLoanApplicationId,
      showFacilityOverview: false,
      showAdditionalInfoRequired: false,
    });
    refetch();
  };

  const currencyCode =
    currentLoanApplication?.creditLine?.currency?.toUpperCase() === "AED" ? (
      <span className="cx-font-aed-font cx-text-text-primary cx-font-bold cx-text-2xl">
        D
      </span>
    ) : (
      currentLoanApplication?.creditLine?.currency?.toUpperCase() || ""
    );

  const isLOC =
    currentLoanApplication?.loanProduct?.type ===
      LoanProductType.LOC_INVOICE_DISCOUNTING ||
    currentLoanApplication?.loanProduct?.type ===
      LoanProductType.LOC_PAYABLE_FINANCING;
  const { useProfitRateTerminology: shouldUseProfitRateTerminology } =
    useLoanProductConfigFlags(
      (currentLoanApplication?.loanProduct?.type ||
        currentLoanApplication?.loanType) as LoanProductType | undefined
    );

  const {
    requestedFacility,
    approvedFacility,
    outstandingAmount,
    availableAmount,
    lateFee,
    availabilityPercentage,
    hasAvailabilityData,
    duration,
  } = useMemo(() => {
    const requestedFacility =
      currentLoanApplication?.creditLine?.requestedLimit || 0;
    const approvedFacility =
      currentLoanApplication?.approvedAmount ||
      currentLoanApplication?.creditLine?.approvedAmount ||
      0;
    const rawAvailableAmount =
      currentLoanApplication?.creditLine?.availableAmount;
    const availableAmount = rawAvailableAmount;
    const lateFee = currentLoanApplication?.creditLine?.lateFee || 0;
    const outstandingAmount =
      currentLoanApplication?.creditLine?.outstandingAmount || 0;
    const availabilityPercentage =
      rawAvailableAmount != null && approvedFacility > 0
        ? Math.round((rawAvailableAmount / approvedFacility) * 100)
        : 0;
    const duration = currentLoanApplication?.creditLine?.duration
      ? `${currentLoanApplication.creditLine.duration} ${
          currentLoanApplication?.creditLine?.durationType ?? "months"
        }`
      : "N/A";
    return {
      requestedFacility,
      approvedFacility,
      outstandingAmount,
      lateFee,
      availableAmount,
      availabilityPercentage,
      hasAvailabilityData: rawAvailableAmount != null,
      duration,
    };
  }, [currentLoanApplication]);

  const isWaitingForDisbursement =
    currentLoanApplication?.caseStatus ===
    LoanApplicationCaseStatus.WAITING_FOR_DISBURSEMENT;

  const isAdditionalInfoStatus =
    currentLoanApplication?.losPlatform === LOSPlatform.CREDIBLEX
      ? currentLoanApplication?.caseStatus ===
        LoanApplicationCaseStatus.ADDITIONAL_INFO_REQUIRED
      : currentLoanApplication?.status ===
        LoanApplicationStatus.ADDITIONAL_INFO_REQUESTED;

  const isInReviewStatus = IN_REVIEW_STATUSES.includes(
    currentLoanApplication?.caseStatus as LoanApplicationCaseStatus
  );

  const valueMap = useMemo(
    () => ({
      requestedFacility,
      approvedFacility,
      availableAmount,
      duration,
      outstandingAmount,
      lateFee,
    }),
    [
      requestedFacility,
      approvedFacility,
      availableAmount,
      duration,
      outstandingAmount,
      lateFee,
    ]
  );

  const displayItems = useMemo(() => {
    const caseStatus = currentLoanApplication?.caseStatus;
    const items = caseStatus
      ? CASE_STATUS_DISPLAY_MAP[caseStatus] || DEFAULT_DISPLAY_ITEMS
      : DEFAULT_DISPLAY_ITEMS;

    return items.map((item) => ({
      ...item,
      value: valueMap[item.valueType],
    }));
  }, [currentLoanApplication?.caseStatus, valueMap]);

  const showDrawdownButton = useMemo(() => {
    if (
      !isLOC ||
      currentLoanApplication?.losPlatform === LOSPlatform.BIZ2X ||
      !currentLoanApplication?.caseStatus
    ) {
      return false;
    }
    return shouldIssueDrawdown(currentLoanApplication?.caseStatus);
  }, [
    isLOC,
    currentLoanApplication?.losPlatform,
    currentLoanApplication?.caseStatus,
  ]);

  const isDrawdownDisabled =
    (currentLoanApplication?.drawdownEligibility != null &&
      !currentLoanApplication?.drawdownEligibility.isValid) ||
    currentLoanApplication?.caseStatus ===
      LoanApplicationCaseStatus.PAST_MATURITY;

  const handleApplyForDrawdown = async () => {
    if (!isLOC || !currentLoanApplicationId) return;

    if (isAdditionalInfoStatus) {
      await update({
        currentLoanApplicationId,
        currentLoanApplication,
        showAdditionalInfoRequired: true,
      });
      return;
    }

    await update({
      currentLoanApplicationId,
      currentLoanApplication,
      showFacilityOverview: false,
      showDrawdownForm: true,
    });
  };

  const dateDisplay = useMemo(
    () =>
      currentLoanApplication
        ? getLoanDateDisplay(currentLoanApplication)
        : null,
    [currentLoanApplication]
  );

  return (
    <>
      <div className="cx-max-w-[960px] cx-mx-auto cx-px-4 sm:cx-px-6">
        <div className="cx-flex cx-flex-col sm:cx-flex-row sm:cx-items-center sm:cx-justify-between cx-gap-3 cx-pb-4">
          <div className="cx-flex cx-items-center cx-gap-2">
            <button
              onClick={handleBack}
              className="cx-flex cx-items-center cx-text-text-secondary hover:cx-text-text-primary cx-transition-colors"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="cx-w-5 cx-h-5 cx-text-blue-600" />
            </button>
            <h1 className="cx-text-xl cx-font-semibold cx-text-text-primary">
              Facility overview
            </h1>
          </div>

          {showDrawdownButton && (
            <button
              onClick={handleApplyForDrawdown}
              disabled={isDrawdownDisabled}
              className={clsx(
                "cx-rounded-full cx-px-4 cx-py-2 cx-text-sm cx-font-medium cx-transition-colors cx-w-full sm:cx-w-auto cx-shrink-0 cx-whitespace-nowrap",
                isDrawdownDisabled
                  ? "cx-bg-gray-300 cx-text-gray-500 cx-cursor-not-allowed"
                  : "cx-bg-interaction-button-primary-default cx-text-white hover:cx-bg-interaction-button-primary-hover"
              )}
            >
              Apply for new drawdown
            </button>
          )}
        </div>
      </div>

      <div className="cx-pb-8">
        <div className="cx-max-w-[960px] cx-mx-auto cx-px-4 sm:cx-px-6">
          <div className="cx-bg-white cx-rounded-lg cx-shadow-sm cx-border cx-border-neutral-light">
            <div className="cx-px-6 cx-py-3 cx-border-b cx-border-neutral-light">
              <div className="cx-flex cx-flex-col sm:cx-flex-row sm:cx-items-stretch sm:cx-justify-between cx-gap-2">
                <div className="cx-flex cx-flex-col cx-my-1.5 cx-min-w-0">
                  {isLOC && (
                    <div className="cx-flex cx-items-center cx-gap-2">
                      <span className="cx-inline-block cx-w-2 cx-h-2 cx-rounded-full cx-bg-green-500" />
                      <span className="cx-text-[12px] cx-text-text-secondary">
                        Line of credit
                      </span>
                    </div>
                  )}
                  <div className="cx-flex cx-items-center cx-gap-3 cx-min-w-0">
                    <div className="cx-text-lg cx-font-medium cx-text-text-primary cx-min-w-0 cx-truncate">
                      {currentLoanApplication?.loanProduct?.displayName ??
                        "Facility"}
                    </div>
                    {currentLoanApplication?.caseStatus ? (
                      <div
                        className={`cx-inline-flex cx-items-center cx-rounded-full cx-px-3 cx-py-1 cx-text-xs cx-font-medium cx-shrink-0 cx-whitespace-nowrap ${
                          LoanApplicationCaseStatusLabelsForCreditLine[
                            currentLoanApplication.caseStatus
                          ]?.displayClasses || ""
                        }`}
                      >
                        {LoanApplicationCaseStatusLabelsForCreditLine[
                          currentLoanApplication.caseStatus
                        ]?.label || "-"}
                      </div>
                    ) : (
                      <div
                        className={`cx-inline-flex cx-items-center cx-rounded-full cx-px-3 cx-py-1.5 cx-text-xs cx-font-medium cx-text-[#8E6A00] cx-bg-[#FCF4D6] cx-shrink-0 cx-whitespace-nowrap`}
                      >
                        Submitted
                      </div>
                    )}
                  </div>
                </div>
                <div className="cx-text-sm cx-text-text-tertiary cx-flex cx-flex-wrap cx-items-center cx-gap-x-1 cx-gap-y-1">
                  <span>ID #{currentLoanApplication?.externalId}</span>
                  <span className="cx-text-text-secondary">•</span>
                  <span
                    className={clsx(
                      "cx-text-sm cx-text-text-tertiary",
                      !dateDisplay?.startsWith("Due date") &&
                        "cx-text-text-secondary"
                    )}
                    style={
                      dateDisplay?.startsWith("Due date")
                        ? { color: "#A2191F" }
                        : undefined
                    }
                  >
                    {dateDisplay ?? "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {isWaitingForDisbursement && (
              <div className="cx-px-6 cx-py-4 cx-space-y-3 cx-border-b cx-border-neutral-light">
                <div className="cx-flex cx-items-center cx-gap-2 cx-text-sm sm:cx-text-base">
                  <span className="cx-text-text-tertiary">
                    Processing fee inclusive of VAT:
                  </span>
                  <span className="cx-flex cx-items-center cx-gap-1 cx-text-text-primary cx-font-bold">
                    {currencyCode}
                    {processingFee != null
                      ? formatAmount(processingFee)
                      : "N/A"}
                  </span>
                </div>

                <div className="cx-flex cx-flex-col md:cx-flex-row cx-items-stretch md:cx-items-center cx-gap-3 cx-border cx-border-neutral-light cx-bg-white cx-p-3 cx-shadow-sm">
                  <div className="cx-flex cx-items-center cx-gap-2">
                    <FirstAbuDhabiBankLogo className="cx-w-8 cx-h-8" />
                    <div className="cx-font-medium">Mashreq Bank</div>
                  </div>
                  <div className="cx-hidden md:cx-block cx-flex-1" />
                  <div className="cx-text-sm cx-text-[#666] cx-flex cx-items-center cx-flex-wrap cx-gap-x-3">
                    <span>Beneficiary name: CREDIBLE X LIMITED</span>
                    <span className="cx-hidden md:cx-inline">|</span>
                    <span
                      className="cx-flex cx-items-center cx-gap-2"
                      style={{ position: "relative" }}
                    >
                      <span>IBAN: {IBAN_TEXT}</span>
                      <button
                        type="button"
                        onClick={handleCopyIban}
                        className="cx-inline-flex cx-items-center cx-justify-center cx-w-6 cx-h-6 cx-rounded cx-text-text-secondary hover:cx-text-text-primary"
                        title={ibanCopied ? "Copied!" : "Copy IBAN"}
                        aria-label={ibanCopied ? "Copied!" : "Copy IBAN"}
                      >
                        {ibanCopied ? (
                          <GreenTickIcon className="cx-w-4 cx-h-4 cx-text-green-600" />
                        ) : (
                          <CopyToClipboard className="cx-w-4 cx-h-4" />
                        )}
                      </button>
                    </span>
                  </div>
                </div>

                <div className="cx-flex cx-items-start cx-justify-between cx-gap-3">
                  <div className="cx-text-sm cx-flex cx-items-center cx-gap-2 cx-pt-3">
                    <InfoIcon
                      className="cx-text-red-600"
                      width={18}
                      height={18}
                    />
                    <span className="cx-text-red-700 cx-font-light">
                      Please pay the processing fee in the bank details
                      specified above to enable your credit facility
                    </span>
                  </div>
                  {/* todo: enable after the slack notification part is done
                  <button
                    className="cx-shrink-0 cx-rounded-btn cx-border cx-border-neutral-light cx-bg-white hover:cx-bg-neutral-50 cx-text-blue-600 cx-text-sm cx-font-medium cx-px-3 cx-py-2"
                    onClick={() => setConfirmPaidOpen(true)}
                  >
                    Already paid?
                  </button> */}
                </div>
              </div>
            )}
            {currentLoanApplication?.caseStatus &&
              !isAdditionalInfoStatus &&
              !isInReviewStatus &&
              displayItems.some((item) => item.showProgressBar) && (
                <>
                  <div className="cx-flex cx-flex-col cx-items-center cx-justify-center cx-py-10 cx-px-6">
                    <LoanDashboardProgress
                      value={hasAvailabilityData ? availabilityPercentage : 100}
                      valueText={hasAvailabilityData ? undefined : "-"}
                      label="Available"
                      size="large"
                      showAmount
                      labelPosition="top"
                      invertFill
                      amountContent={
                        <>
                          {currencyCode}{" "}
                          {hasAvailabilityData
                            ? formatAmount(availableAmount ?? 0)
                            : "-"}
                        </>
                      }
                    />
                  </div>
                  <Divider />
                </>
              )}

            <div className="cx-flex cx-flex-col sm:cx-flex-row cx-divide-y sm:cx-divide-y-0 sm:cx-divide-x cx-divide-neutral-light sm:cx-h-28 cx-py-4 cx-my-4">
              {displayItems.map((item, index) => (
                <div
                  key={index}
                  className="cx-flex cx-flex-col cx-items-center cx-justify-center cx-py-4 sm:cx-py-8 cx-px-4 cx-flex-1"
                >
                  <div
                    className="cx-text-[#666] cx-text-base cx-font-medium cx-mb-2"
                    style={
                      item.headingColor
                        ? { color: item.headingColor }
                        : undefined
                    }
                  >
                    {item.label}
                  </div>
                  <div className="cx-flex cx-items-baseline cx-gap-x-1">
                    {item.showCurrency && (
                      <span
                        className="cx-font-aed-font cx-font-bold cx-text-3xl"
                        style={
                          item.textColor ? { color: item.textColor } : undefined
                        }
                      >
                        D
                      </span>
                    )}
                    <div
                      className="cx-text-text-primary cx-font-bold cx-text-3xl"
                      style={
                        item.textColor ? { color: item.textColor } : undefined
                      }
                    >
                      {item.valueType === "duration"
                        ? item.value || "N/A"
                        : item.value != null
                        ? formatAmount(item.value as number)
                        : "-"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {currentLoanApplication?.caseStatus != null &&
              displayItems.some((item) => item.showInterestRate) && (
                <div className="cx-px-6 cx-py-6 cx-border-t cx-border-neutral-light">
                  <div className="cx-flex cx-items-start cx-justify-center cx-gap-3">
                    <div className="cx-text-sm cx-flex cx-items-start cx-gap-2 cx-py-1 cx-min-w-0">
                      <span className="cx-shrink-0 cx-flex cx-items-center cx-h-5">
                        <InfoIcon
                          className="cx-text-blue-600"
                          width={18}
                          height={18}
                        />
                      </span>
                      <span className="cx-text-[#666] cx-text-sm cx-min-w-0">
                        {withProfitRateTerminology(
                          `A ${currentLoanApplication?.creditLine?.interestRate}% monthly interest is charged on the approved invoice amount`,
                          shouldUseProfitRateTerminology
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
        {drawdowns.length > 0 && (
          <div className="cx-max-w-[960px] cx-mx-auto">
            <InvoiceListing
              loanApplicationId={currentLoanApplicationId}
              initialDrawdowns={drawdowns}
            />
          </div>
        )}
      </div>

      {showAdditionalInfoRequired && <AdditionalInfoForm />}

      <Modal
        isOpen={confirmPaidOpen}
        onClose={() => setConfirmPaidOpen(false)}
        className="cx-max-w-[500px] cx-max-h-[300px] cx-w-full"
      >
        <div className="cx-flex cx-flex-col cx-items-center cx-justify-center cx-px-12 cx-pt-0 cx-pb-3">
          <div className="cx-text-[28px] cx-font-bold cx-mb-3 cx-text-center">
            Confirm payment
          </div>
          <div className="cx-text-lg cx-text-[#7D8592] cx-mb-8 cx-text-center">
            Have you already paid the processing fee? <br />
            Please confirm before we proceed.
          </div>
          <div className="cx-flex cx-justify-center cx-gap-6 cx-w-full">
            <Button
              secondary
              applyBorder
              label="Cancel"
              className="cx-min-w-[180px] cx-h-14 cx-text-lg cx-font-medium cx-border-[2px] hover:cx-bg-neutral-50"
              onClick={() => setConfirmPaidOpen(false)}
            />
            <Button
              label={"Yes, I've paid"}
              className="cx-min-w-[180px] cx-h-14 cx-text-lg cx-font-medium cx-text-white"
              onClick={() => {
                setConfirmPaidOpen(false);
                handleBack();
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FacilityOverview;
